import { Button, ButtonDropdown, Container, ContentLayout, StatusIndicator, Tabs, Link, Box } from "@amzn/awsui-components-react";
import React, { useState, Suspense } from "react";
import { useSelector } from "react-redux";
import { selectIsEffectiveAdmin, selectIsEffectiveSupportAdmin } from '../../../utilities/adminUtils';
import { useHistory, useLocation } from "react-router-dom";
import CPBoardView from "../components/CPBoardView";
import Apps from "src/Home/Apps";
import TeamApplications from "src/Home/TeamApplications";
import CustomMultipleResourcePage from "../components/CustomMultipleResourcePage";
import CustomizeTabsModal from "src/Home/CustomizeTabsModal";
import { AI_AGENT_ENABLED } from "src/Ateam-BI/AutoMate/constants";
import useAgentChat from "src/Ateam-BI/AutoMate/hooks/useAgentChat";
import useSearchAgent from "src/Ateam-BI/AutoMate/hooks/useSearchAgent";
import useAgentSuggestions from "src/Ateam-BI/AutoMate/hooks/useAgentSuggestions";
import { usePersonaContext } from "src/Ateam-BI/AutoMate/context/PersonaContext";
import { closePortalDefaultTabs } from "../constants";
import { v4 as uuidv4 } from 'uuid';
import { createPreference, updatePreference } from "src/utilities/AppSyncPlusAPIs";
import { createCustomTabOfOwner, updateCustomTabOfOwner, deleteCustomTabOfOwner } from "src/utilities/CloseAPIs";
import { configureGenericAppSync } from "src/context/AuthContextUtility";

// Lazy-loaded AI components — only fetched when AI_AGENT_ENABLED is true
const AISearchBar = React.lazy(() => import('src/Ateam-BI/AutoMate/components/AISearchBar/AISearchBar'));
const SearchPopup = React.lazy(() => import('src/Ateam-BI/AutoMate/components/SearchPopup/SearchPopup'));
const AutoMateBubble = React.lazy(() => import('src/Ateam-BI/AutoMate/components/AutoMateBubble/AutoMateBubble'));

// Simple error boundary so a failed lazy import doesn't crash the whole page
class AIErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) { console.error('AIErrorBoundary caught:', error, info); }
  render() { return this.state.hasError ? null : this.props.children; }
}

export const TeamPage = ({selectedCPView, viewAccess, addOrEditAccess, tabs, allTabs, allCustomTabs, hiddenTabs, tabOrderLoaded, orderedTabs, updateTab, resetHref, hideOrUnhideCustomTab, ownerTabConfigInfo, currentTabPreferences, setOwnerTabConfigInfo, setTabs, setAllTabs, setAllCustomTabs, setHiddenTabs, setCurrentTabPreferences}) => {
    const history = useHistory();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const currentStage = useSelector((globalState) => globalState.auth.currentStage)
    const [activeTabId, setActiveTabId] = useState(searchParams.get('tab')?.replaceAll('%2F', '/') || null);
    const [activeHref, setActiveHref] = useState()

    const isPortalAdmin = useSelector(selectIsEffectiveAdmin)
    const isSupportAdmin = useSelector(selectIsEffectiveSupportAdmin)
    const currentUser = useSelector((globalState) => globalState.auth.userId)

    // Customize Tabs state
    const [customizeTabsVisible, setCustomizeTabsVisible] = useState(false)
    const [modalLoading, setModalLoading] = useState(false)
    const [errorText, setErrorText] = useState()
    const [selectedTabControlsSegmentId, setSelectedTabControlsSegmentId] = useState("add")
    const [selectedUpdateOrDeleteTab, setSelectedUpdateOrDeleteTab] = useState()
    const [selectedTabToUnhide, setSelectedTabToUnhide] = useState()
    const [updateTabNameInput, setUpdateTabNameInput] = useState('')
    const [newTabName, setNewTabName] = useState('')
    const [selectedTabType, setSelectedTabType] = useState()

    // When tabs load for the current team, determine the active tab.
    // If URL has a tab param and it's visible, use it. Otherwise pick first visible tab.
    React.useEffect(() => {
      if (tabOrderLoaded && tabs?.length > 0) {
        const tabFromUrl = new URLSearchParams(location.search).get('tab')?.replaceAll('%2F', '/');
        if (tabFromUrl && tabs.some(t => t.id === tabFromUrl)) {
          setActiveTabId(tabFromUrl);
        } else {
          // No tab param or tab is hidden — pick first visible
          const firstVisibleTab = tabs[0];
          if (firstVisibleTab) {
            setActiveTabId(firstVisibleTab.id);
            history.replace(`/ClosePortal/Teams/${selectedCPView?.value}?tab=${firstVisibleTab.id?.replaceAll('/', '%2F')}`);
          }
        }
      }
    }, [tabOrderLoaded, tabs]);
    const addOrUpdateTabConfig = async (tabConfig) => {
      if (selectedCPView) {
        const newPreferenceId = uuidv4();
        const information = { "closePortalOwner": selectedCPView?.value, "tabConfiguration": { "closePortalTabs": tabConfig } }
        const label = `Tab configuration for Close Portal Page ${selectedCPView?.label}`
        if (ownerTabConfigInfo?.length <= 0) {
          createPreference(newPreferenceId, 'CLOSE_PORTAL_TABS_CONFIG', label, selectedCPView?.value, 'CLOSE_PORTAL', currentUser, information, null,
            (response) => {
              setCurrentTabPreferences([{ preferenceId: newPreferenceId, information: information }]);
              const tabConfigInfo = information?.tabConfiguration?.closePortalTabs
              setOwnerTabConfigInfo(tabConfigInfo)
              setTabs(tabConfigInfo?.filter((tab) => tab?.visible))
              setAllTabs(tabConfigInfo)
              setAllCustomTabs(tabConfigInfo?.filter((tab) => tab?.isDefault === false))
              setHiddenTabs(tabConfigInfo?.filter((tab) => tab?.visible === false))
            },
            (error) => { console.error('Error creating preferences:', error); setModalLoading(false); }
          );
        } else {
          const currentPreferenceId = currentTabPreferences[0]?.preferenceId
          updatePreference(currentPreferenceId, label, currentUser, information,
            (response) => {
              setCurrentTabPreferences([{ preferenceId: currentPreferenceId, information: information }]);
              const tabConfigInfo = information?.tabConfiguration?.closePortalTabs
              setOwnerTabConfigInfo(tabConfigInfo)
              setTabs(tabConfigInfo?.filter((tab) => tab?.visible))
              setAllTabs(tabConfigInfo)
              setAllCustomTabs(tabConfigInfo?.filter((tab) => tab?.isDefault === false))
              setHiddenTabs(tabConfigInfo?.filter((tab) => tab?.visible === false))
            },
            (error) => { console.error('Error updating preferences:', error); setModalLoading(false); }
          )
        }
      }
    }

    const resetModalParams = () => {
      setCustomizeTabsVisible(false)
      setModalLoading(false)
      setErrorText()
      setSelectedTabControlsSegmentId("add")
      setNewTabName('')
      setSelectedUpdateOrDeleteTab()
      setUpdateTabNameInput('')
      setSelectedTabToUnhide()
    }

    const localHideOrUnhideTab = (tabId, hide) => {
      const tabConfig = (allTabs || [])?.map((tab) => {
        if (tab?.id === tabId) {
          return { ...tab, visible: !hide }
        }
        return tab
      })
      addOrUpdateTabConfig(tabConfig)
    }

    const addNewCustomTab = async () => {
      if (newTabName?.trim() !== '' && selectedTabType) {
        if ((allCustomTabs || [])?.length >= 10) { setErrorText('Cannot create more than 10 custom tabs!'); return; }
        if (allTabs?.some(tab => tab.id === newTabName?.trim() || tab.label === newTabName?.trim())) { setErrorText('A tab with same name exists!'); return; }
        setModalLoading(true)
        await createCustomTabOfOwner(newTabName?.trim(), selectedCPView?.value, currentUser, selectedTabType?.value, function onSuccess() {
          const tabConfig = allTabs?.concat({ "id": newTabName?.trim(), "label": newTabName?.trim(), "isDefault": false, "order": closePortalDefaultTabs?.length + 1, "tabType": selectedTabType?.value, "visible": true })
          addOrUpdateTabConfig(tabConfig)
          resetModalParams()
          configureGenericAppSync()
        }, function onFailure() { setModalLoading(false); setErrorText('Failed to create tab.'); configureGenericAppSync(); })
      } else { setErrorText('All fields are required!'); }
    }

    const updateExistingCustomTab = async () => {
      if (!selectedUpdateOrDeleteTab) { setErrorText('Please select a tab to update!'); return; }
      if (updateTabNameInput?.trim() === '') { setErrorText('Please provide a new tab name!'); return; }
      if (allTabs?.some(tab => tab.id === updateTabNameInput?.trim() || tab.label === updateTabNameInput?.trim())) { setErrorText('A tab with same name exists!'); return; }
      setModalLoading(true)
      const selectedTab = allTabs?.find(tab => tab?.id === selectedUpdateOrDeleteTab?.value);
      if (selectedTab?.isDefault) {
        const tabConfig = allTabs?.map((tab) => tab?.id === selectedUpdateOrDeleteTab?.value ? { ...tab, "label": updateTabNameInput?.trim() } : tab)
        addOrUpdateTabConfig(tabConfig)
        resetModalParams()
      } else {
        await updateCustomTabOfOwner(selectedCPView?.value, selectedUpdateOrDeleteTab?.value, updateTabNameInput?.trim(), function onSuccess() {
          const tabConfig = allTabs?.map((tab) => tab?.id === selectedUpdateOrDeleteTab?.value ? { ...tab, "id": updateTabNameInput?.trim(), "label": updateTabNameInput?.trim() } : tab)
          addOrUpdateTabConfig(tabConfig)
          resetModalParams()
          configureGenericAppSync()
        }, function onFailure() { setModalLoading(false); setErrorText('Failed to update tab.'); configureGenericAppSync(); })
      }
    }

    const deleteExistingCustomTab = async () => {
      if (!selectedUpdateOrDeleteTab) { setErrorText('Please select a tab to delete!'); return; }
      setModalLoading(true)
      await deleteCustomTabOfOwner(selectedCPView?.value, selectedUpdateOrDeleteTab?.value, function onSuccess() {
        const tabConfig = allTabs?.filter((tab) => selectedUpdateOrDeleteTab?.value !== tab?.id)
        addOrUpdateTabConfig(tabConfig)
        resetModalParams()
        setActiveTabId("HOME")
        configureGenericAppSync()
      }, function onFailure() { setModalLoading(false); setErrorText('Failed to delete tab.'); configureGenericAppSync(); })
    }

    const handleReorderTabs = async (newOrder) => {
      if (selectedCPView?.value) {
        setModalLoading(true);
        const fixedTabsConfig = [{ "id": "HOME", "label": "Home", "isDefault": true, "order": 1, "tabType": "BOARD", "visible": true }]
        const tabConfig = fixedTabsConfig?.concat(newOrder)
        await addOrUpdateTabConfig(tabConfig)
        setCustomizeTabsVisible(false); setModalLoading(false); setErrorText(); setSelectedTabControlsSegmentId("add");
      }
    }

    const [aiSearchValue, setAiSearchValue] = useState("")
    const [aiPopupVisible, setAiPopupVisible] = useState(false)
    const [chatBubbleOpen, setChatBubbleOpen] = useState(false)
    const [selectedSuggestion, setSelectedSuggestion] = useState("")
    const { messages: aiMessages, isStreaming: aiIsStreaming, waitingReason: aiWaitingReason, handleChatSubmit: aiHandleChatSubmit, handleCancel: aiHandleChatCancel, handleNewChat: aiHandleNewChat } = useAgentChat();
    // Hoisted search-agent store. Owns the search results so they survive
    // closing/reopening the popup (the popup itself is presentational now).
    const {
        query: aiSearchQuery,
        searchAnswer: aiSearchAnswer,
        searchSources: aiSearchSources,
        quickSearchResults: aiQuickSearchResults,
        isSearching: aiIsSearching,
        searchError: aiSearchError,
        searchSessionId: aiSearchSessionId,
        searchConversationId: aiSearchConversationId,
        runSearch: aiRunSearch,
    } = useSearchAgent();
    // Dynamic suggestions for the AISearchBar. The hook owns the 1000ms
    // debounce, the searchAgent call, the onAgentResponse subscription, and
    // the LRU cache. Its conversationId is independent of the SearchPopup's
    // primary `searchAgent` call (Requirement 10.9) because the popup uses
    // `useAgentChat` (sendAgentMessage) and this hook fires its own
    // searchAgent invocations per debounce — no shared conversationId.
    const { suggestions: aiSuggestions, isLoading: aiSuggestionsLoading, onQueryChange: aiOnQueryChange } = useAgentSuggestions();
    // Use the shared persona context instead of calling usePersona directly
    // to avoid duplicate API calls (PersonaProvider already owns the fetch).
    const { personaText: aiPersonaText, userName: aiUserName } = usePersonaContext();

    const handleAIAsk = (query) => {
        // Every submission — typed (single or multi-word) or selected from
        // the autocomplete — fires both the search (`aiRunSearch`) and the
        // chatbot (`aiHandleChatSubmit`). The SearchPopup hosts both UIs.
        // Search state lives in `useSearchAgent` (above the popup) so results
        // persist across close/reopen without re-firing.
        setChatBubbleOpen(false);
        setSelectedSuggestion(query);
        setAiPopupVisible(true);
        aiRunSearch(query);
        aiHandleChatSubmit(query);
    }
    
    return (     
        <ContentLayout>

        {activeTabId === 'HOME' && AI_AGENT_ENABLED && (
            <Box margin={{ top: 'm' }}>
              <AIErrorBoundary>
                <Suspense fallback={null}>
                  <AISearchBar
                    placeholder="Search or Ask a Question"
                    value={aiSearchValue}
                    onChange={(newValue) => {
                      setAiSearchValue(newValue);
                      aiOnQueryChange(newValue);
                    }}
                    onAsk={handleAIAsk}
                    loading={aiIsStreaming}
                    suggestions={aiSuggestions}
                    suggestionsLoading={aiSuggestionsLoading}
                    onClose={aiPopupVisible ? () => setAiPopupVisible(false) : null}
                    onOpen={!aiPopupVisible && selectedSuggestion ? () => setAiPopupVisible(true) : null}
                  />
                </Suspense>
              </AIErrorBoundary>
            </Box>
        )}
        {activeTabId === 'HOME' && AI_AGENT_ENABLED && aiPopupVisible && (
            <div className="dropdown-popup">
              <div className="dropdown-popup-inner">
                <AIErrorBoundary>
                  <Suspense fallback={null}>
                    <SearchPopup onClose={() => setAiPopupVisible(false)} query={aiSearchQuery} searchAnswer={aiSearchAnswer} searchSources={aiSearchSources} quickSearchResults={aiQuickSearchResults} isSearching={aiIsSearching} searchError={aiSearchError} searchSessionId={aiSearchSessionId} searchConversationId={aiSearchConversationId} personaText={aiPersonaText} userName={aiUserName} messages={aiMessages} isStreaming={aiIsStreaming} waitingReason={aiWaitingReason} onChatSubmit={aiHandleChatSubmit} onChatCancel={aiHandleChatCancel} onNewChat={aiHandleNewChat} />
                  </Suspense>
                </AIErrorBoundary>
              </div>
            </div>
        )}

        <Container>
            {addOrEditAccess && activeTabId !== 'AppsLibrary' && (tabs || [])?.length > 0 && (
              <Box float="right" margin={{ top: 's', bottom: 's' }}>
                <Link
                  variant="primary"
                  fontSize="heading-m"
                  onFollow={() => setCustomizeTabsVisible(true)}
                >
                  Customize Tabs
                </Link>
              </Box>
            )}
            <br />
            {tabOrderLoaded === true && (tabs || [])?.length > 0 ? (<Tabs
                tabs={(tabs || [])?.map((tab) => (
                    {
                    label: tab?.label,
                    id: tab?.id,
                    disabled: (tab?.id !== 'HOME' && !(tabOrderLoaded)),
                    content: (
                        tab?.tabType === 'BOARD' ? (
                        <CPBoardView
                            pageType={tab?.id}
                            tabType={tab?.tabType}
                            owner={selectedCPView}
                            openResource={updateTab}
                            viewAccess={viewAccess}
                            addOrEditAccess={addOrEditAccess}
                        />
                        ) : tab?.tabType === 'APPS' ? (
                        <Apps closePortalowner={selectedCPView} addOrEditAccess={addOrEditAccess} />
                        ) : tab?.tabType === 'TEAM_APPS' ? (
                        <TeamApplications
                            key={selectedCPView?.value}
                            ldapGroup={selectedCPView?.owner_identifier}
                            teamName={selectedCPView?.label}
                            stage={currentStage}
                        />
                        ) : tab?.tabType === 'LIST' ? (
                        <CustomMultipleResourcePage
                            pageType={tab?.id}
                            tabType={tab?.tabType}
                            owner={selectedCPView}
                            activeHref={activeHref}
                            viewAccess={viewAccess}
                            addOrEditAccess={addOrEditAccess}
                            resetHref={resetHref}
                        />
                        ) : (
                        <></>
                        )
                    ),
                    action: (
                        (isPortalAdmin ||
                        (addOrEditAccess && tab?.id !== 'HOME' && tab?.id !== 'AI/ML')) &&
                        <ButtonDropdown
                        items={[{ id: "hide-tab", text: "hide tab for team" }]}
                        ariaLabel="tab actions"
                        variant="icon"
                        onItemClick={(event) => {
                            if (event?.detail?.id === 'hide-tab') {
                            localHideOrUnhideTab(tab?.id, true);
                            }
                        }}
                        expandToViewport={true}
                        />
                    )
                    }
                ))}
                activeTabId={activeTabId}
                onChange={(event) => {
                    setActiveHref()
                    const selectedTab = (tabs || [])?.find(t => t.id === event.detail.activeTabId);
                    if (event.detail.activeTabId === 'AppsLibrary' || selectedTab?.tabType === 'APPS') {
                    history.push(`/ClosePortal/Teams/${selectedCPView?.value}?tab=AppsLibrary`)
                    } else {
                    history.push(`/ClosePortal/Teams/${selectedCPView?.value}?tab=${event.detail.activeTabId?.replaceAll('/', '%2F')}`)
                    }
                    setActiveTabId(event.detail.activeTabId)
                }}
            />) : tabOrderLoaded === true && (tabs || [])?.length === 0 ? (
              <Box textAlign="center" padding={{ top: "xxl", bottom: "xxl" }}>
                <Box variant="h3" color="text-status-inactive" padding={{ bottom: "s" }}>All tabs are hidden</Box>
                <Box variant="p" color="text-body-secondary" padding={{ bottom: "m" }}>
                  {addOrEditAccess
                    ? 'Use "Customize Tabs" to unhide tabs and configure your workspace.'
                    : 'No tabs are currently visible. Contact your team admin.'}
                </Box>
                {addOrEditAccess && (
                  <Button variant="primary" onClick={() => setCustomizeTabsVisible(true)}>
                    Customize Tabs
                  </Button>
                )}
              </Box>
            ) : (
                <StatusIndicator colorOverride="blue" type="loading">
                    Fetching Tab Configuration For The Selected Close Portal Team
                </StatusIndicator>
            )}

            <CustomizeTabsModal
              visible={customizeTabsVisible}
              onDismiss={resetModalParams}
              modalLoading={modalLoading}
              errorText={errorText}
              allTabs={allTabs}
              allCustomTabs={allCustomTabs}
              hiddenTabs={hiddenTabs}
              selectedTabControlsSegmentId={selectedTabControlsSegmentId}
              setSelectedTabControlsSegmentId={setSelectedTabControlsSegmentId}
              newTabName={newTabName}
              setNewTabName={setNewTabName}
              selectedUpdateOrDeleteTab={selectedUpdateOrDeleteTab}
              setSelectedUpdateOrDeleteTab={setSelectedUpdateOrDeleteTab}
              updateTabNameInput={updateTabNameInput}
              setUpdateTabNameInput={setUpdateTabNameInput}
              selectedTabToUnhide={selectedTabToUnhide}
              setSelectedTabToUnhide={setSelectedTabToUnhide}
              onAddTab={addNewCustomTab}
              onUpdateTab={updateExistingCustomTab}
              onDeleteTab={deleteExistingCustomTab}
              selectedTabType={selectedTabType}
              setSelectedTabType={setSelectedTabType}
              onUnhideTab={async () => {
                if (selectedTabToUnhide) {
                  localHideOrUnhideTab(selectedTabToUnhide?.value, false)
                  resetModalParams()
                } else {
                  setErrorText('Please select a tab to unhide!')
                }
              }}
              visibleTabs={tabs}
              onReorderTabs={handleReorderTabs}
              orderedTabs={orderedTabs}
              selectedCPView={selectedCPView}
            />
        </Container>
        </ContentLayout>
)}
